<template>
  <div class="space-y-4">
    <SearchBookingInput
      v-if="isAnyBookingScope(props.scope)"
      @update-booking="handleSelectBooking"
    />
    <p v-if="isAnyBookingScope(props.scope) && !selectedBooking" class="text-sm text-obsidian-600">
      {{ t('selectBookingHint') }}
    </p>
    <div class="grid grid-cols-2 items-center gap-4">
      <Select
        v-if="!isPaymentProps(props) || !props.payment?.counterpartyType || showCounterpartySelect"
        v-model="selectedCounterpartyType"
        :label="t('counterpartyType.label')"
        :options="counterpartyTypeOptions"
        :placeholder="t('selectCounterpartyType')"
        empty-value-to="undefined"
      />
      <div v-else class="flex items-end gap-2">
        <LabeledText :label="t('counterpartyType.label')" class="min-w-0 flex-1 gap-2">
          <div class="flex items-center gap-2">
            <div>{{ t(`counterpartyTypes.${counterpartyType}`) }}</div>
            <div class="flex items-center gap-2 text-xs text-obsidian-600">
              <span>–</span>
              <button :class="link()" @click="handleChangeCounterpartyType">
                {{ t('change') }}
              </button>
            </div>
          </div>
        </LabeledText>
      </div>
      <Select
        v-model="allocationMode"
        :label="t('allocationMode')"
        :options="allocationModeOptions"
      />
    </div>
    <div class="flex flex-col gap-4">
      <template v-if="allocationMode === 'select' && selectorScope">
        <LedgerEntrySelector
          v-model="selectedLedgerEntries"
          :amount-to-select-key="isPaymentProps(props) ? 'amountToPay' : 'amountToInvoice'"
          :scope="selectorScope"
          :disabled="isAllocating"
          :allow-allocation-outside-of-bounds="true"
          :include-entirely-allocated="true"
          @update-total-amount="selectedAmountToAllocateSum = $event"
        />
      </template>
      <StatusBanner v-if="exceedsUnallocated" variant="warning">
        {{
          t('exceedsUnallocatedWarning', {
            selected: formatCurrency(selectedAmountToAllocateSum / 100),
            unallocated: formatCurrency(toAllocate),
          })
        }}
      </StatusBanner>
      <div class="flex items-center justify-end gap-4">
        <div class="font-bold">{{ t('openAmount') }} {{ formatCurrency(toAllocate) }}</div>
        <CVButton
          :disabled="allocateDisabled"
          :is-loading="isAllocating"
          variant="default"
          @click="handleAllocate"
        >
          {{ t('allocate') }}
        </CVButton>
        <CVButton :disabled="isAllocating" variant="secondary" @click="$emit('cancel')">
          {{ t('cancel') }}
        </CVButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { CounterpartyType } from '@/entities/ledger/ledger-entry.entity.ts';
import type { SelectOption } from '@/components/Select.vue';
import Select from '@/components/Select.vue';
import { useCounterpartyTypeOptions } from '@/hooks/use-counterparty-type-options';
import CVButton from '@/components/buttons/CVButton.vue';
import LedgerEntrySelector from './LedgerEntrySelector.vue';
import {
  useAllocateInvoice,
  useAllocatePayment,
  useInvoiceAllocations,
  usePaymentAllocations,
} from '@/queries/use-ledger.ts';
import type { Payment } from '@/entities/payments/payment.entity.ts';
import { sumBy } from 'lodash';
import { formatCurrency } from '@/utils/format-numbers.ts';
import LabeledText from '../LabeledText.vue';
import { Alert } from '@/services/alert.service.ts';
import type { Invoice } from '@/entities/invoices/invoice.entity.ts';
import type {
  BookingScopeDto,
  InvoiceScopeDto,
  LedgerEntriesScopeDto,
} from '@carvia/ros-client-types';
import {
  isAnyBookingScope,
  isBookingScope,
  type AllocationScope,
  type BookingScope,
} from './utils/booking-or-invoice-scope';
import SearchBookingInput from '@/components/booking-forms/SearchBookingInput.vue';
import type { ManyBookingsBooking } from '@/entities/bookings/many-bookings-booking.entity';
import { link } from '@carvia/theme/lib/cva/link';
import StatusBanner from '@/components/StatusBanner.vue';

type AllocationMode = 'all' | 'select';

type PaymentProps = {
  payment: Payment;
  invoice?: never;
  scope: AllocationScope;
};
type InvoiceProps = {
  invoice: Invoice;
  payment?: never;
  scope: BookingScope;
};
type Props = PaymentProps | InvoiceProps;

const props = defineProps<Props>();

const isPaymentProps = (props: Props): props is PaymentProps => props.payment !== undefined;

const emit = defineEmits<{
  (e: 'success'): void;
  (e: 'cancel'): void;
}>();

const { t } = useI18n();

const selectedCounterpartyType = ref<CounterpartyType | undefined>(
  (isPaymentProps(props) && props.payment.counterpartyType) || CounterpartyType.CUSTOMER,
);
const allocationMode = ref<AllocationMode>('all');
const showCounterpartySelect = ref(false);

const handleChangeCounterpartyType = async () => {
  const { isConfirmed } = await Alert.fire({
    title: t('changeCounterpartyTypeTitle'),
    text: t('changeCounterpartyTypeWarning'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('change'),
    cancelButtonText: t('cancel'),
  });
  if (isConfirmed) {
    showCounterpartySelect.value = true;
  }
};
const selectedLedgerEntries = ref<{ id: string; customAmount?: number }[]>([]);
const selectedAmountToAllocateSum = ref(0);
const selectedBooking = ref<ManyBookingsBooking>();

const handleSelectBooking = (booking: ManyBookingsBooking | undefined) => {
  selectedBooking.value = booking;
};

const { mutateAsync: allocatePayment, isPending: isAllocatingPayment } = useAllocatePayment();
const { mutateAsync: allocateInvoice, isPending: isAllocatingInvoice } = useAllocateInvoice();
const isAllocating = computed(() => isAllocatingPayment.value || isAllocatingInvoice.value);
const { data: paymentAllocations } = usePaymentAllocations(
  computed(() => (isPaymentProps(props) ? props.payment.id : undefined)),
);
const { data: invoiceAllocations } = useInvoiceAllocations(
  computed(() => (!isPaymentProps(props) ? props.invoice.id : undefined)),
);
const allocations = computed(() => {
  return isPaymentProps(props) ? paymentAllocations.value : invoiceAllocations.value;
});

const toAllocate = computed(
  () =>
    (isPaymentProps(props) ? props.payment.amount : props.invoice.totalPositionSum) -
    sumBy((allocations.value ?? []) as { amount: number }[], 'amount') / 100,
);

const exceedsUnallocated = computed(() => {
  if (allocationMode.value !== 'select') return false;
  const remainingCents = Math.round(toAllocate.value * 100);
  const selectedCents = selectedAmountToAllocateSum.value;
  if (remainingCents >= 0) return selectedCents > remainingCents;
  return selectedCents < remainingCents;
});

const counterpartyType = computed(
  () =>
    (isPaymentProps(props) && !showCounterpartySelect.value && props.payment.counterpartyType) ||
    selectedCounterpartyType.value ||
    CounterpartyType.CUSTOMER,
);

const selectorScope = computed(() => {
  if (isAnyBookingScope(props.scope)) {
    if (!selectedBooking.value) return undefined;
    return {
      bookingId: selectedBooking.value.id,
      counterpartyType: counterpartyType.value,
    };
  }
  if (isBookingScope(props.scope)) {
    return {
      bookingId: props.scope.bookingId,
      counterpartyType: counterpartyType.value,
    };
  }
  return { invoiceId: props.scope.invoiceId };
});

const allocateDisabled = computed(
  () =>
    (isAnyBookingScope(props.scope) && !selectedBooking.value) ||
    (allocationMode.value === 'select' && selectedLedgerEntries.value.length === 0),
);

const counterpartyTypeOptions = useCounterpartyTypeOptions();

const allocationModeOptions = computed<SelectOption[]>(() => [
  { label: t('allocateAll'), value: 'all' },
  { label: t('selectEntries'), value: 'select' },
]);

watch([allocationMode, counterpartyType, () => selectedBooking.value?.id], () => {
  selectedLedgerEntries.value = [];
  selectedAmountToAllocateSum.value = 0;
});

const handleAllocate = async () => {
  if (allocationMode.value === 'select' && selectedLedgerEntries.value.length === 0) return;
  const overAllocating = exceedsUnallocated.value;
  const { isConfirmed } = await Alert.fire({
    title: overAllocating ? t('exceedsUnallocatedTitle') : undefined,
    text: overAllocating
      ? t('exceedsUnallocatedText', {
          selected: formatCurrency(selectedAmountToAllocateSum.value / 100),
          unallocated: formatCurrency(toAllocate.value),
        })
      : t('reallyAllocatePaymentText'),
    icon: overAllocating ? 'warning' : 'question',
    buttons: [
      { text: t('allocate'), propertyName: 'isConfirmed', style: 'default' },
      { text: t('cancel'), propertyName: 'isDismissed', style: 'secondary' },
    ],
  });
  if (!isConfirmed) {
    return;
  }
  if (isAnyBookingScope(props.scope) && !selectedBooking.value) return;
  const scope: BookingScopeDto | LedgerEntriesScopeDto | InvoiceScopeDto =
    allocationMode.value === 'select'
      ? {
          type: 'ledgerEntries',
          ledgerEntries: selectedLedgerEntries.value,
        }
      : isAnyBookingScope(props.scope)
        ? {
            type: 'booking',
            bookingId: selectedBooking.value!.id,
            counterpartyType: counterpartyType.value,
          }
        : isBookingScope(props.scope)
          ? {
              type: 'booking',
              bookingId: props.scope.bookingId,
              counterpartyType: counterpartyType.value,
            }
          : {
              type: 'invoice',
              invoiceId: props.scope.invoiceId,
            };
  if (isPaymentProps(props)) {
    await allocatePayment({
      paymentId: props.payment.id,
      scope,
    });
  } else {
    await allocateInvoice({
      invoiceId: props.invoice.id,
      scope: scope as BookingScopeDto | LedgerEntriesScopeDto,
    });
  }
  emit('success');
};
</script>

<i18n lang="json">
{
  "en": {
    "selectCounterpartyType": "Select counterparty type",
    "changeCounterpartyTypeTitle": "Really assign to another counterparty?",
    "changeCounterpartyTypeWarning": "You should only change the counterparty if you are sure the payment was assigned to the wrong counterparty. Do you want to change it?",
    "change": "Change",
    "allocationMode": "Allocation",
    "selectBookingHint": "Search for a booking to allocate this payment to its ledger entries.",
    "allocateAll": "Allocate to all",
    "selectEntries": "Select entries manually",
    "allocate": "Allocate",
    "allocateError": "Allocation failed",
    "openAmount": "Open amount",
    "reallyAllocatePaymentText": "Are you sure, you want to allocate this payment?",
    "exceedsUnallocatedTitle": "Allocate more than the unallocated amount?",
    "exceedsUnallocatedWarning": "The selected amount {selected} exceeds the unallocated amount {unallocated}.",
    "exceedsUnallocatedText": "The selected amount {selected} exceeds the unallocated amount {unallocated}. Do you still want to allocate?"
  },
  "de": {
    "selectCounterpartyType": "Gegenpartei auswählen",
    "changeCounterpartyTypeTitle": "Wirklich einer anderen Gegenpartei zuordnen?",
    "changeCounterpartyTypeWarning": "Du solltest die Gegenpartei nur ändern, wenn du sicher bist, dass die Zahlung der falschen Gegenpartei zugeordnet wurde. Möchtest du sie wirklich ändern?",
    "change": "Ändern",
    "allocationMode": "Zuordnung",
    "selectBookingHint": "Suche eine Buchung, um diese Zahlung ihren Forderungen zuzuordnen.",
    "allocateAll": "Alles zuordnen",
    "selectEntries": "Selbst auswählen",
    "allocate": "Zuordnen",
    "allocateError": "Zuordnung fehlgeschlagen",
    "openAmount": "Offener Betrag",
    "reallyAllocatePaymentText": "Bist du sicher, dass du diese Zahlung zuordnen möchtest?",
    "exceedsUnallocatedTitle": "Mehr zuordnen als der offene Betrag?",
    "exceedsUnallocatedWarning": "Der ausgewählte Betrag {selected} übersteigt den noch nicht zugeordneten Betrag {unallocated}.",
    "exceedsUnallocatedText": "Der ausgewählte Betrag {selected} übersteigt den noch nicht zugeordneten Betrag {unallocated}. Trotzdem zuordnen?"
  },
  "es": {
    "selectCounterpartyType": "Seleccionar tipo de contraparte",
    "changeCounterpartyTypeTitle": "¿Asignar a otra contraparte?",
    "changeCounterpartyTypeWarning": "Solo debe cambiar la contraparte si está seguro de que el pago se asignó mal. ¿Desea cambiarla?",
    "change": "Cambiar",
    "allocationMode": "Imputación",
    "selectBookingHint": "Busca una reserva para imputar este pago a sus partidas.",
    "allocateAll": "Imputar a todo",
    "selectEntries": "Seleccionar partidas manualmente",
    "allocate": "Imputar",
    "allocateError": "La imputación ha fallado",
    "openAmount": "Importe pendiente",
    "reallyAllocatePaymentText": "¿Seguro que desea imputar este pago?",
    "exceedsUnallocatedTitle": "¿Imputar más que el importe no imputado?",
    "exceedsUnallocatedWarning": "El importe seleccionado {selected} supera el importe no imputado {unallocated}.",
    "exceedsUnallocatedText": "El importe seleccionado {selected} supera el importe no imputado {unallocated}. ¿Desea imputar de todos modos?"
  }
}
</i18n>
